/* jshint esversion: 6, -W097 */
/* globals require, module */
'use strict';

const constants = require('@utils/_constants');

/**
 * ProductService is responsible for making Product detail page api calls and model level calculations
 *
 * @module enervee_utils.product
 */
ProductService.$inject = [
    '$q',
    '$log',
    '$http',
    '$state',
    '$location',
    '$window',
    'AccountAuthService',
    'AccountSideNavService',
    'AnalyticsService',
    'CookiesService',
    'FavProductsService',
    'GetFetchService',
    'IndexService',
    'ProductApiConsumerService',
    'SignupService',
    'SwiftypeService',
    'UserApiConsumerService',
    'UtilService',
];
function ProductService(
    $q,
    $log,
    $http,
    $state,
    $location,
    $window,
    AccountAuthService,
    AccountSideNavService,
    AnalyticsService,
    CookiesService,
    FavProductsService,
    GetFetchService,
    IndexService,
    ProductApiConsumerService,
    SignupService,
    SwiftypeService,
    UserApiConsumerService,
    UtilService
) {
    var _pub = {},
        _productDetailPromises = {},
        _productsByMpn = {},
        _productsByMpnPromises = {},
        _priceChanges = {},
        _sponsoredContent = '';

    _pub.extractProductIdFromUrl = function(productInfoUrl) {
        return parseInt(productInfoUrl.split("-")[0]);
    };

    /**
     * Gets a product by productId
     *
     * @param {String} productId
     * @param {Boolean} [force] if true, re-fetch the data even if it's
     * already been fetched once
     * @returns {$q.promise}
     */
    _pub.getProduct = function(productId, force) {
        var url = ProductApiConsumerService.getFullUrl('api/1.0/products/' + productId + '/');
        return GetFetchService.get(['products', productId], url, {force: force});
    };

    /**
     * Gets a product from swiftype by productId
     *
     * @param {String} productId
     * @param {String} categoryId
     * @param {String} categoryCode
     * @param {Boolean} [force] if true, re-fetch the data even if it's
     * already been fetched once
     * @returns {$q.promise}
     */
    _pub.getSwiftypeProduct = SwiftypeService.getProduct;

    /**
     * Gets styles from swiftype by parentId
     *
     * @param {String} parentId
     * @param {String} categoryId
     * @param {String} categoryCode
     * @param {Boolean} [force] if true, re-fetch the data even if it's
     * already been fetched once
     * @returns {$q.promise}
     */
    _pub.getSwiftypeProductStyles = function(parentId, categoryCode, force) {
        var requestUrl = ProductApiConsumerService.getFullUrl('api/1.0/swiftype/search/'),
            requestOptions = {
                method: 'POST',
                url: requestUrl,
                data: {
                   document_types: [categoryCode],
                   filters: {}
               },
            };

        requestOptions.data.filters[categoryCode] = {parent_id: parentId};

        return GetFetchService.get(['stylesByParent', parentId], requestOptions, {
            force: force,
            responseReturnFunction: function(result) {
                var styles = result.data.records[categoryCode];
                // save the `styles` array under ['stylesByParent', parentId]
                styles.forEach(saveProduct);
                return styles;
            },
        });
    };

    // Save to GetFetchService for not getting again
    var saveProduct = function(product){
        var existingProduct = GetFetchService.getRaw(['products', product.id]);

        if (!existingProduct) {
            product.fullName = _pub.getFullName(product);
            GetFetchService.update(['products', product.id], product);
        }
    };


    /**
     * Fetches the product based on MPN
     *
     * @param {String} mpn
     * @param {Boolean} [force] if true, re-fetch the data even if it's
     * already been fetched once
     * @returns {$q.promise}
     */
     var fetchProductByMpn = function(mpn, force) {
         if (!_productsByMpnPromises[mpn] || force) {
             var url = ProductApiConsumerService.getFullUrl('api/1.0/products/mpn/' + mpn + '/');
             _productsByMpnPromises[mpn] = ProductApiConsumerService.get(url).then(function(result) {
                 _productsByMpn[mpn] = result.data;
                     saveProduct(result.data);
             }, function() {
                _productsByMpn[mpn] = undefined;
            });
         }
         return _productsByMpnPromises[mpn];
     };

    /**
     * Gets a product by its MPN, rather than its ID
     *
     * @param {String} mpn
     * @param {Boolean} [force] if true, re-fetch the data even if it's
     * already been fetched once
     * @returns {$q.promise}
     */
    _pub.getProductByMpn = function(mpn, force) {
        var deferred = $q.defer();
        if (!_productsByMpn[mpn] || force) {
            fetchProductByMpn(mpn, force).then(function() {
                deferred.resolve(_productsByMpn[mpn]);
            });
        } else {
            deferred.resolve(_productsByMpn[mpn]);
        }
        return deferred.promise;
    };

    /**
     * Helper function to return an array of ids that don't exist as
     * keys in the passed obj
     *
     * @param {String[]} keys
     * @param {Object} obj
     * @returns {String[]}
     */
    var filterNonexistentKeys = function(keys, obj) {
        return keys.filter(function(id) {
            return !(id in obj);
        });
    };

    /**
     * Helper function to get the values of all the keys from the
     * passed obj
     */
    var getAllKeys = function(keys, obj) {
        return keys.map(function(key) {
            return obj[key];
        });
    };

    /**
     * Fetches a list of products
     *
     * @param {String[]} keys
     * @param {Boolean} [force] if true, then re-fetch the data
     * @returns {$q.promise}
     */
    _pub.fetchProducts = function(productIds, force) {
        var deferred = $q.defer();
        // Copy the array of productIds
        var unfetchedProductIds = productIds.slice(0);
        if (!force) {
            // If the `force` parameter is not true, only fetch the
            // products that haven't been fetched yet
            unfetchedProductIds = filterNonexistentKeys(unfetchedProductIds, _productDetailPromises);
        }
        if (unfetchedProductIds.length > 10) {
            $log.warn("The product API currently does not return more than 10 products at a time");
        }
        if (unfetchedProductIds.length > 0) {
            // Create the promise for fetching any products that haven't been fetched already
            var fetchPromise = ProductApiConsumerService.get('/api/1.0/products/', {
                params: {
                    ids_list: unfetchedProductIds.join(','),
                }
            }).then(function(response) {
                response.data.results.forEach(function(product) {
                    GetFetchService.update(['products', product.id], product);
                });
            });
            unfetchedProductIds.forEach(function(id) {
                _productDetailPromises[id] = fetchPromise;
            });
        }
        // Collect a hash of promises that the fetch function is waiting on
        var promises = [];
        productIds.forEach(function(id) {
            promises.push(_productDetailPromises[id]);
        });
        // Wait for all the promises in the array to resole, then resolve
        // the returned promise
        $q.all(promises).then(function() {
            deferred.resolve();
        });
        return deferred.promise;
    };

    var getProductResource = function(resourceKey, productId, endpoint, force) {
        var url = ProductApiConsumerService.getFullUrl('api/1.0/' + endpoint + '/' + productId + '/');
        return GetFetchService.get([resourceKey, productId], url, {force: force});
    };

    /**
     * Gets the recommended products for a specified product
     *
     * @param {Object} product
     * @param {Boolean} [force] if true, then re-fetch the data
     * @returns {$q.promise}
     */
    _pub.getRecommendedProducts = (product, force) => {
        const url = ProductApiConsumerService.getFullUrl('api/1.0/recommended-products/' + product.id + '/');
        const requestOptions = {
            url: url,
            method: 'POST',
            data: {
                category_code: product.category.category_code,
                filters: {
                    external_id: product.id
                }
            }
        };
        return GetFetchService.get(['recommendedProducts', product.id], requestOptions, {
            force: force,
            responseReturnFunction: response => {
                return response.data['records'][product.category.category_code];
            }
        });
    };

    /**
     * Gets the productFeatures for a specified product
     *
     * @param {string} productId
     * @param {Boolean} [force] if true, then re-fetch the data
     * @returns {$q.promise}
     */
    _pub.getProductFeatures = function(productId, force) {
        return getProductResource('productFeatures', productId, 'product-features', force);
    };

    /**
     * Gets the retailer ratings for a specified product
     *
     * @param {string} productId
     * @param {Boolean} [force] if true, then re-fetch the data
     * @returns {$q.promise}
     */
    _pub.getRetailersRatings = function(productId, force) {
        return getProductResource('retailersRatings', productId, 'retailers-ratings', force);
    };

    /**
     * Gets the expert reviews for a specified product
     *
     * @param {string} productId
     * @param {Boolean} [force] if true, then re-fetch the data
     * @returns {$q.promise}
     */
    _pub.getExpertReviews = function(productId, force) {
        return getProductResource('expertReviews', productId, 'expert-reviews', force);
    };

    /**
     * Gets the extra details for a specified product that can't be
     * indexed in swiftype
     *
     * @param {string} productId
     * @param {Boolean} [force] if true, then re-fetch the data
     * @returns {$q.promise}
     */
    _pub.getExtraDetails = function(productId, force) {
        return getProductResource('extraDetails', productId, 'product-details', force);
    };

    /**
     * Gets the product images for a specified product
     *
     * @param {string} productId
     * @param {Boolean} [force] if true, then re-fetch the data
     * @returns {$q.promise}
     */
    _pub.getProductImages = function(productId, force) {
        return getProductResource('productImages', productId, 'product-images', force);
    };

    /**
     * Gets the product images cloudinary ids for a specified product.
     * @param productId
     * @returns list of cloudinary ids {Array}
     */
    _pub.getProductImagesForCloudinary = function (productId) {
        return _pub.getProductImages(productId).then(function (productImages) {
            if (!angular.isArray(productImages)) {
                return [];
            }
            return productImages.map(function (image) {
                return image.cloudinary_id;
            });
        });
    };

    /**
     * First makes an api call to to like the product, and then gets the details of the product to update
     * the page accordingly
     *
     * @param productId
     * @returns {*}
     */
    _pub.likeProduct = function(productId, modalText = '') {
        var defer = $q.defer();
        var _isAuthenticated = AccountAuthService.isAuthenticated();

        if (_isAuthenticated) {
            UserApiConsumerService.post('api/1.0/favorite-product/' + productId + '/').then(function() {
                FavProductsService.clearStoredFavoriteProducts();
                defer.resolve();
            });
        } else {
            IndexService.setSignupModalHeaders('fav_product', {priceDropText: modalText});
            IndexService.openModal('#modal-enter-email');

            AccountAuthService.saveSearchCallBack = null;

            AccountAuthService.onAuthChange(function () {
                _pub.likeProduct(productId).then(function () {
                    defer.resolve();
                });
            }, constants.EVENT_TYPE_LIKE);

            SignupService.signUpModalOpenedCallBack(constants.EVENT_TYPE_LIKE);
        }
        return defer.promise;
    };

    /**
     * Helper function to get details page configuration value from
     * details_page_configuration object.
     * @param key
     */
    _pub.getDetailsPageConfig = function (key) {
        return UtilService.getSiteSetting('details_page_configuration', {})[key];
    };

    /**
     * Fetches the price changes for the given product
     *
     * @param productId
     * @returns {*}
     */
    _pub.fetchPriceChanges = function(productId) {

        return ProductApiConsumerService.get('/api/1.0/price-drop/' + productId + '/').then(function(response){
            _priceChanges = response.data;
        });

    };

    /**
     * return price drop side bar information
     *
     * @returns {*}
     */
    _pub.getPriceDropSideBarInformation = function () {
        return _priceChanges.price_drop_information;
    };


    /**
     * Gets the price changes for the given product
     *
     * @returns {*}
     */
    _pub.getPriceChangePointsStr = function() {

        return _priceChanges.price_drop_information.coordinates;

    };

    /**
     * Takes a productId and queries the CMS backend to get the product's sponsored content
     *
     * @param productId
     * @returns {*}
     */
    _pub.fetchSponsoredContent = function(productId) {

        return $http.get(UtilService.getSiteSetting('cms_base_url') + 'product-content/product-content/' + productId + '/').then(function(response){
            _sponsoredContent = response.data.content;
        }, function(error){
            _sponsoredContent = '';
        });

    };

    /**
     * Gets the product's sponsored content
     *
     * @returns {*}
     */
    _pub.getSponsoredContent = function() {

        return _sponsoredContent;
    };

    /**
     * Set's the ad settings for product pages
     *
     * @returns {*}
     */
    _pub.setAdSettings = function(adArray) {
        var siteAdSettings = UtilService.getSiteSetting('ad_settings');
        if (siteAdSettings === null) {
            return null;
        }
        var adPrefix = "details_";
        var adSettings = {};
        for (var i = 0; i < adArray.length; i++) {
            adSettings[adArray[i]] = siteAdSettings[adPrefix + adArray[i]];
        }

        return adSettings;
    };

    /**
     * Retrieves the placeholder URL for the product if it exists, otherwise returns "undefined"
     *
     * @returns {string}
     */
    _pub.getPlaceholderImageUrl = function(productData) {
        return UtilService.getSetting('productPlaceholders')[productData.category.id];
    };

    /**
     * Sets the default image sizes for the product gallery
     * Define a couple of sizes for getting images from Cloudinary, so instead of
     * making 5 requests for 5 sizes of the same image, we re-use and scale these.
     * Only needed for the product detail page due to the number of image variations.
     *
     * @returns {Object}
     */
    _pub.setProductImageSizes = function() {
        return {
            "small": 200, // Main image, on-scroll mini header image, gallery thumbnails
            "large": 435 // Gallery large image
        };
    };

    /**
     * Returns a recommended product from the given product's recommended ids list.
     * Returns a reject promise if the recommended ids list doesn't exist, or the id matches the given product's id.
     * @param product
     * @returns {*}
     */
    _pub.getRecommendedProduct = function (product) {
        if (('recommended_ids' in product) &&
        (product.recommended_ids !== null) &&
        (product.recommended_ids.length > 0)) {
            for (var i = 0; i < product.recommended_ids.length; i++) {
                var alternativeProductId = product.recommended_ids[i];
                if (parseInt(alternativeProductId) !== product.id) {
                    return _pub.getSwiftypeProduct(
                         alternativeProductId,
                         product.category_id,
                         product.category.category_code
                    );
                }
            }
            return $q.reject();
        } else {
            return $q.reject();
        }
    };

    /**
     * Returns icon class depending on the given car's atv_type.
     * @param car
     * @returns {{fuelIconName: string, additionalFuelIconName: string}}
    */
    _pub.getResourceIcons = function (car) {
        var icons = {
            fuelIconName: "",
            additionalFuelIconName: ""
        };
        switch(car.atv_type) {
            case "EV":
                icons.fuelIconName = "mp-fuel-electric";
                break;
            case "Diesel":
                icons.fuelIconName = "mp-fuel-diesel";
                break;
            case "FFV":
                icons.fuelIconName = "mp-fuel-flex";
                break;
            case "Hydrogen":
                icons.fuelIconName = "mp-fuel-hydrogen";
                break;
            case "Hybrid":
                icons.fuelIconName = "mp-fuel-gas";
                icons.additionalFuelIconName = "mp-fuel-battery";
                break;
            case "Plug-in Hybrid":
                icons.fuelIconName = "mp-fuel-gas";
                icons.additionalFuelIconName = "mp-fuel-electric";
                break;
            default:
                icons.fuelIconName = "mp-fuel-gas";
        }
        return icons;
    };

    /**
     * Returns year and name for the given car.
     * @param car
     * @returns Year + Name: string
     */
    _pub.getFullName = function (car) {
        return car.model_year + " " + car.name;
    };

    /**
     * Returns a promise which will resolve with a list of cars matching the ids requested.
     *
     * @param {Array<String|Number>} carIds - An array of ids for cars products
     * @param {Boolean} [force=false] - An optional boolean to force re-requesting cars data
     */
    _pub.getCars = function (carIds, force) {
        var key = carIds.join(',');
        var searchParams = {
            document_types: ['cars'],
            filters: {'cars': {'external_id': carIds}}
        };

        var urlConfigs = {
            url: ProductApiConsumerService.getFullUrl('api/1.0/swiftype/search/'),
            method: 'POST',
            data: searchParams,
        };
        return GetFetchService.get(['carComparisons', key], urlConfigs, {
            force: force,
            responseReturnFunction: function (response) {
                return response.data.records.cars;
            }
        }).catch(function () {
            $log.error('Product could not be retrieved from swiftype!');
        });
    };

    /**
     * Check car type and get title for fuel cost
     * @param carType
     * @returns Type of fuel car use: string
     */
    _pub.getFuelCostTitle = function (carType) {
        if (carType && (carType.toLowerCase() === "ev")) {
            return "Electricity";
        }
        if (carType && (carType.toLowerCase() === "plug-in hybrid")) {
            return "Electricity + Fuel";
        }
        return "Fuel";
    };

    /**
     * Returns true if the given productId is favourited by the user .
     * @param productId
     * @returns forceReload {boolean}
     */
    _pub.isFavored = function(productId, forceReload) {
        return FavProductsService.fetchFavoriteProductsIdList(forceReload).then(function (favProductIds) {
            var favProducts = favProductIds.filter(function (favProduct) {
                return favProduct.id === productId;
            });
            return favProducts.length > 0;
        }, function () {
            IndexService.setHeaderAlerts([{
                type: 'error',
                message: 'Failed to load favorite products.',
            }]);
            return false;
        });
    };

    /**
     * Formats an offer to be tracked with uniform data.
     *
     * @param {Object} offer
     * @param {String} offer.store_name
     * @param {Boolean} offer.sponsored
     * @param {String|Number} offer.price
     * @param {String|Number} offer.total_price
     * @param {String} offer.link
     * @return {Object}
     */
    _pub.getOfferTrackingDetails = function (offer) {
        return {
            name: offer.store_name,
            sponsored: offer.sponsored,
            price: parseFloat(offer.price),
            shipping: parseFloat(offer.total_price) - parseFloat(offer.price),
            url: offer.link,
            review: false
        };
    };

    _pub.redirectToRetailer = function (product, offer, index) {
        var offerDetails = _pub.getOfferTrackingDetails(offer);
        offerDetails.position = index + 1;
        AnalyticsService.reportEvent('Offer Clicked', offerDetails, product);

        var offerLink = UtilService.getAffiliateLink(offer);
        $window.open(offerLink, '_blank');
    };

    return _pub;
}

module.exports = ProductService;
